/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO:
/*
 What I'm struggling with is data type of ConvertCaseFunction.
 In order to implement recursive functions, I need to define type of target as any,
 but ConvertCaseFunction can't take any type as an argument because
 it's supposed to be applied to string data only.
 It's possible to add conditional process like if (isString(str)), but typescript
 won't understand the type checks and eventually the following script will cause
 type check error like "str may not be string and then replace method can't be called".
 */
import { isNumber } from '@scripts/utils/type-utils';

export type ConvertCaseOptions = {
  trimEdges?: boolean;
  compressRepeats?: boolean;
};

export type ConvertCaseFunction = ({
  target,
  options,
}: {
  target: string,
  options?: ConvertCaseOptions,
}) => string;

export const camelToKebab: ConvertCaseFunction = ({
  target,
  options = {
    trimEdges: false,
    compressRepeats: false,
  },
}) => {
  const { trimEdges = false, compressRepeats = false } = options;

  let kebab = target.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
  if (trimEdges) kebab = kebab.replace(/^-+|-+$/g, '');
  if (compressRepeats) kebab = kebab.replace(/^-+|-+$/g, '-');

  return kebab;
};

export const kebabToCamel: ConvertCaseFunction = ({
  target,
  options = {
    trimEdges: false,
    compressRepeats: false,
  },
}) => {
  const { trimEdges = false, compressRepeats = false } = options;

  let camel = target;
  if (trimEdges) camel = camel.replace(/^-+|-+$/g, '');
  camel = camel.replace(/-([a-z0-9])/g, (_, letter) => letter.toUpperCase());
  if (compressRepeats) camel = camel.replace(/-/g, '');

  return camel;
};

export const camelToSnake: ConvertCaseFunction = ({
  target,
  options = {
    trimEdges: false,
    compressRepeats: false,
  },
}) => {
  const { trimEdges = false, compressRepeats = false } = options;

  let snake = target.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  if (trimEdges) snake = snake.replace(/^_+|_+$/g, '');
  if (compressRepeats) snake = snake.replace(/_+/g, '_');

  return snake;
};

export const snakeToCamel: ConvertCaseFunction = ({
  target,
  options = {
    trimEdges: false,
    compressRepeats: false,
  },
}) => {
  const { trimEdges = false, compressRepeats = false } = options;

  let camel = target;
  if (trimEdges) camel = camel.replace(/^_+|_+$/g, '');
  camel = camel.replace(/_([a-z0-9])/g, (_, letter) => letter.toUpperCase());
  if (compressRepeats) camel = camel.replace(/_/g, '');

  return camel;
};

export const calcTextLifetime = ({
  text,
  minLifetime = 1600,
  maxLifetime = 10000,
  lettersPerSec = 12,
}: {
  text: string,
  minLifetime?: number,
  maxLifetime?: number,
  lettersPerSec?: number,
}): number => {
  const lifetime = Math.round(text.length / lettersPerSec) * 1000;
  return Math.min(Math.max(minLifetime, lifetime), maxLifetime);
};

export const calcWarningLifetime = ({
  text,
  minLifetime = 3000,
  maxLifetime = 10000,
  lettersPerSec = 12,
}: {
  text: string,
  minLifetime?: number,
  maxLifetime?: number,
  lettersPerSec?: number,
}): number => calcTextLifetime(
  {
    text,
    minLifetime,
    maxLifetime,
    lettersPerSec,
  },
);

export const doubleByteToSingleByte = (text: string): string => (
  text
    .replace(/[！-～]/g, (elem) => String.fromCharCode(elem.charCodeAt(0) - 0xFEE0))
    .replace(/”/g, '"')
    .replace(/’/g, '\'')
    .replace(/‘/g, '`')
    .replace(/￥/g, '\\')
    .replace(/\u3000/g, ' ') // Whitespace in double byte
    .replace(/〜/g, '~')
);

export const normalizeWhitespaces = (text: string): string => (
  text.replace(/\s+/g, ' ').trim()
);

export const normalizeInputString = (input: string): string => {
  const singleByteInput: string = doubleByteToSingleByte(input);
  const endsWithWhiteSpace: boolean = input.endsWith(' ');
  return normalizeWhitespaces(singleByteInput) + (endsWithWhiteSpace ? ' ' : '');
};

export const normalizeInputStringArray = ({
  target,
  keepDuplicates = false,
  caseSensitive = false,
}: {
  target: string[],
  keepDuplicates: boolean,
  caseSensitive: boolean,
}): string[] => {
  if (!target) return target;

  const normalizedTarget = target.map((text) => normalizeInputString(text));

  if (!keepDuplicates) {
    const seen = new Set();
    return normalizedTarget.filter((text) => {
      if (seen.has(caseSensitive ? text : text.toLowerCase())) {
        return false;
      }
      seen.add(caseSensitive ? text : text.toLowerCase());
      return true;
    });
  }

  return normalizedTarget;
};

export const getEmailDomain = (email: string): string => {
  const match = email.match(/^[^@]+@(.+)$/);
  return match ? `@${match[1]}` : '';
};

export const truncate = ({
  text,
  maxLength,
  ellipsis = '...',
}: {
  text: string;
  maxLength: number;
  ellipsis: string;
}) => (
  text.length > maxLength
    ? text.slice(0, maxLength - ellipsis.length) + ellipsis
    : text
);

export const formatNumber = ({
  value,
  maxDecimals = 2,
  fallback = '',
}: {
  value: any;
  maxDecimals: number;
  fallback: string | null;
}): string => {
  if (!isNumber(value)) {
    return String(fallback === null ? value : fallback);
  }

  if (Number.isInteger(value)) return String(value);

  const decimalsInString = value.toFixed(maxDecimals);
  const [, intPart, fracPart] = decimalsInString.match(/^(\d+)\.(\d+)$/)!;

  const fractionalIsAllZeros = /^0+$/.test(fracPart);
  if (fractionalIsAllZeros) return decimalsInString;

  // Trim trailing zero at a time until neither "0" nor empty remains
  return `${intPart}.${fracPart.replace(/0+$/, '')}`;
};
